import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TimeWindow, PreferredDaysGrid } from '@vagusx/clients';
import { dayToken } from '@vagusx/shared';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ── Constants ─────────────────────────────────────────────────────────────────

/** Canonical day order / labels used as `dayTimes` keys and `days` entries. */
const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const DEFAULT_GRID: PreferredDaysGrid = {
  startTime: '07:00',
  endTime: '19:00',
  stepMinutes: 30,
};

const STEP_OPTIONS = [15, 30, 60] as const;

// ── Pure time helpers (exported for testing) ────────────────────────────────────

/** "HH:MM" → minutes since midnight. */
export function timeToMin(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Minutes since midnight → "HH:MM" (zero-padded). */
export function minToTime(min: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, min));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// `dayToken` is re-exported from @vagusx/shared so existing imports/tests of
// this module keep working while the canonical implementation lives in one place.
export { dayToken };

/** "HH:MM" (24h) → friendly "h:mm AM/PM". */
export function formatTimeLabel(t: string): string {
  const min = timeToMin(t);
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const period = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/** 30-minute time options spanning a day, for the From/To range dropdowns. */
const TIME_OPTIONS: Array<{ value: string; label: string }> = Array.from({ length: 48 }, (_, i) => {
  const value = minToTime(i * 30);
  return { value, label: formatTimeLabel(value) };
});

/** Time options including `current` even if it's not on a 30-minute boundary. */
function timeOptionsWith(current: string): Array<{ value: string; label: string }> {
  if (TIME_OPTIONS.some((o) => o.value === current)) return TIME_OPTIONS;
  return [...TIME_OPTIONS, { value: current, label: formatTimeLabel(current) }].sort(
    (a, b) => timeToMin(a.value) - timeToMin(b.value)
  );
}

/**
 * Number of grid rows for a [startMin, endMin) range at the given step.
 * Returns 0 for a non-positive range.
 */
export function rowCount(startMin: number, endMin: number, step: number): number {
  if (step <= 0 || endMin <= startMin) return 0;
  return Math.floor((endMin - startMin) / step);
}

/** Set of row indices whose cell overlaps any of the windows. */
export function windowsToOnRows(
  windows: TimeWindow[],
  startMin: number,
  step: number,
  rows: number
): Set<number> {
  const on = new Set<number>();
  for (let i = 0; i < rows; i++) {
    const cellStart = startMin + i * step;
    const cellEnd = cellStart + step;
    if (windows.some((w) => cellStart < timeToMin(w.endTime) && cellEnd > timeToMin(w.startTime))) {
      on.add(i);
    }
  }
  return on;
}

/** Merge a set of "on" row indices into contiguous time windows. */
export function onRowsToWindows(on: Set<number>, startMin: number, step: number): TimeWindow[] {
  const sorted = [...on].sort((a, b) => a - b);
  const windows: TimeWindow[] = [];
  let runStart: number | null = null;
  let prev: number | null = null;
  for (const idx of sorted) {
    if (runStart === null) {
      runStart = idx;
    } else if (prev !== null && idx !== prev + 1) {
      windows.push({
        startTime: minToTime(startMin + runStart * step),
        endTime: minToTime(startMin + (prev + 1) * step),
      });
      runStart = idx;
    }
    prev = idx;
  }
  if (runStart !== null && prev !== null) {
    windows.push({
      startTime: minToTime(startMin + runStart * step),
      endTime: minToTime(startMin + (prev + 1) * step),
    });
  }
  return windows;
}

/**
 * The portions of `windows` that fall OUTSIDE the visible [startMin, endMin)
 * range. These are preserved across edits so a day's windows outside the
 * current grid view are never silently dropped when the user paints.
 */
export function windowsOutsideRange(
  windows: TimeWindow[],
  startMin: number,
  endMin: number
): TimeWindow[] {
  const out: TimeWindow[] = [];
  for (const w of windows) {
    const ws = timeToMin(w.startTime);
    const we = timeToMin(w.endTime);
    if (ws < startMin)
      out.push({ startTime: w.startTime, endTime: minToTime(Math.min(we, startMin)) });
    if (we > endMin) out.push({ startTime: minToTime(Math.max(ws, endMin)), endTime: w.endTime });
  }
  return out;
}

/** Sort and merge overlapping/adjacent windows into a minimal set. */
export function mergeWindows(windows: TimeWindow[]): TimeWindow[] {
  const ranges = windows
    .map((w) => [timeToMin(w.startTime), timeToMin(w.endTime)] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of ranges) {
    const last = merged[merged.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else merged.push([s, e]);
  }
  return merged.map(([s, e]) => ({ startTime: minToTime(s), endTime: minToTime(e) }));
}

/**
 * Human-readable per-day summary of the current selection, in week order.
 * A preferred day with no windows reads "All day"; otherwise its windows are
 * listed (e.g. "9:00 AM–12:00 PM, 2:00 PM–4:00 PM").
 */
export function summarizeSelection(
  days: string[],
  dayTimes: Record<string, TimeWindow[]> | undefined
): Array<{ day: string; label: string }> {
  const preferred = new Set(days.map(dayToken));
  return DAY_ABBR.filter((abbr) => preferred.has(dayToken(abbr))).map((abbr) => {
    const entry = Object.entries(dayTimes ?? {}).find(([k]) => dayToken(k) === dayToken(abbr));
    const windows = entry?.[1] ?? [];
    return {
      day: abbr,
      label:
        windows.length === 0
          ? 'All day'
          : windows
              .map((w) => `${formatTimeLabel(w.startTime)}–${formatTimeLabel(w.endTime)}`)
              .join(', '),
    };
  });
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface PreferredDaysValue {
  days: string[];
  dayTimes?: Record<string, TimeWindow[]>;
  grid?: PreferredDaysGrid;
}

interface DragState {
  mode: 'add' | 'remove';
  anchor: { day: number; row: number };
  current: { day: number; row: number };
}

/** Labeled time dropdown for the grid's From/To range (themed Select). */
function TimeRangeSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}): React.ReactElement {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-7 text-xs w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {timeOptionsWith(value).map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * Visual weekly availability grid for the `preferredDays` constraint.
 *
 * Drag across cells to paint bookable blocks; drag again over painted cells to
 * clear. Clicking a day header toggles the whole day. Days/times are stored as
 * `days` + per-day `dayTimes` windows:
 *   - no painted cells  → day not preferred (removed from `days`)
 *   - all cells painted → preferred, no window (whole working day)
 *   - some painted      → preferred, explicit windows
 *
 * The visible range and cell size are configurable (stored in `grid`) and are
 * display-only — the slot engine reads `days`/`dayTimes` regardless.
 *
 * `readOnly` turns the grid into a display-only view: painting and the day
 * toggle are disabled (used to preview slots the scheduling agent would offer).
 * `showViewControls` (default true) toggles the From/To/Cell-size row — hide it
 * when the caller fixes the view (e.g. the slots preview sizes the grid to the
 * data). `columnDateLabels` (length 7, Mon→Sun) renders an actual date under
 * each day header, for previews of a specific calendar week. `highlight`
 * marks one window on one day (a slot someone picked elsewhere) and scrolls it
 * into view.
 */
export function PreferredDaysGridEditor({
  value,
  onChange,
  readOnly = false,
  showViewControls = true,
  columnDateLabels,
  highlight,
}: {
  value: PreferredDaysValue;
  onChange: (next: PreferredDaysValue) => void;
  readOnly?: boolean;
  showViewControls?: boolean;
  columnDateLabels?: string[];
  /** One window to mark: a day key and "HH:MM" start/end. */
  highlight?: { day: string; startTime: string; endTime: string } | null;
}): React.ReactElement {
  const grid = value.grid ?? DEFAULT_GRID;
  const startMin = timeToMin(grid.startTime);
  const endMin = timeToMin(grid.endTime);
  const step = grid.stepMinutes;
  const rows = rowCount(startMin, endMin, step);

  const [drag, setDrag] = useState<DragState | null>(null);

  // The highlighted rows: those whose cell overlaps the window.
  const highlightDay = highlight
    ? DAY_ABBR.findIndex((a) => dayToken(a) === dayToken(highlight.day))
    : -1;
  const isHighlighted = (dayIdx: number, rowIdx: number): boolean => {
    if (!highlight || dayIdx !== highlightDay) return false;
    const cellStart = startMin + rowIdx * step;
    return (
      cellStart < timeToMin(highlight.endTime) && cellStart + step > timeToMin(highlight.startTime)
    );
  };
  const highlightRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    highlightRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [highlight]);

  // Normalize stored data to canonical abbreviation keys so 'Mon'/'monday' both work.
  const preferred = useMemo(() => new Set(value.days.map(dayToken)), [value.days]);
  const windowsByDay = useMemo(() => {
    const map = new Map<string, TimeWindow[]>();
    for (const abbr of DAY_ABBR) {
      const entry = Object.entries(value.dayTimes ?? {}).find(
        ([k]) => dayToken(k) === dayToken(abbr)
      );
      if (entry && entry[1].length > 0) map.set(abbr, entry[1]);
    }
    return map;
  }, [value.dayTimes]);

  // Base "on" rows per day (ignoring any in-progress drag preview). Memoized so
  // a drag (which only changes `drag`) doesn't rebuild these Sets per cell.
  const baseOnRowsByDay = useMemo(
    () =>
      DAY_ABBR.map((abbr) => {
        if (!preferred.has(dayToken(abbr))) return new Set<number>();
        const windows = windowsByDay.get(abbr);
        if (!windows) return new Set(Array.from({ length: rows }, (_, i) => i)); // all-day
        return windowsToOnRows(windows, startMin, step, rows);
      }),
    [preferred, windowsByDay, rows, startMin, step]
  );

  /** Is (dayIdx, rowIdx) within the active drag rectangle? */
  const inDragRect = useCallback(
    (dayIdx: number, rowIdx: number): boolean => {
      if (!drag) return false;
      const d0 = Math.min(drag.anchor.day, drag.current.day);
      const d1 = Math.max(drag.anchor.day, drag.current.day);
      const r0 = Math.min(drag.anchor.row, drag.current.row);
      const r1 = Math.max(drag.anchor.row, drag.current.row);
      return dayIdx >= d0 && dayIdx <= d1 && rowIdx >= r0 && rowIdx <= r1;
    },
    [drag]
  );

  /** Effective cell state for rendering (base state with drag preview applied). */
  const cellOn = useCallback(
    (dayIdx: number, rowIdx: number): boolean => {
      if (drag && inDragRect(dayIdx, rowIdx)) return drag.mode === 'add';
      return baseOnRowsByDay[dayIdx]?.has(rowIdx) ?? false;
    },
    [baseOnRowsByDay, drag, inDragRect]
  );

  const isAllDay = useCallback(
    (dayIdx: number): boolean => {
      const abbr = DAY_ABBR[dayIdx]!;
      return preferred.has(dayToken(abbr)) && !windowsByDay.has(abbr);
    },
    [preferred, windowsByDay]
  );

  // Commit the current drag rectangle to the value on pointer release.
  const commitDrag = useCallback(
    (active: DragState) => {
      const d0 = Math.min(active.anchor.day, active.current.day);
      const d1 = Math.max(active.anchor.day, active.current.day);
      const r0 = Math.min(active.anchor.row, active.current.row);
      const r1 = Math.max(active.anchor.row, active.current.row);

      const nextDays = new Set(preferred);
      const nextDayTimes: Record<string, TimeWindow[]> = {};
      // Seed with all existing (canonicalized) windows so untouched days survive.
      for (const [abbr, windows] of windowsByDay) nextDayTimes[abbr] = windows;

      for (let dayIdx = d0; dayIdx <= d1; dayIdx++) {
        const abbr = DAY_ABBR[dayIdx]!;
        const on = new Set(baseOnRowsByDay[dayIdx]);
        for (let r = r0; r <= r1; r++) {
          if (active.mode === 'add') on.add(r);
          else on.delete(r);
        }
        // Build windows for the painted visible range, then merge back any of the
        // day's existing windows that lie OUTSIDE the visible range (so narrowing
        // the From/To view never drops out-of-view windows).
        const visibleWindows = onRowsToWindows(on, startMin, step);
        const offRange = windowsOutsideRange(windowsByDay.get(abbr) ?? [], startMin, endMin);
        const merged = mergeWindows([...offRange, ...visibleWindows]);
        if (merged.length === 0) {
          nextDays.delete(dayToken(abbr));
          delete nextDayTimes[abbr];
        } else {
          nextDays.add(dayToken(abbr));
          nextDayTimes[abbr] = merged;
        }
      }

      // Rebuild canonical days list in week order.
      const days = DAY_ABBR.filter((abbr) => nextDays.has(dayToken(abbr)));
      onChange({ ...value, days, dayTimes: nextDayTimes, grid });
    },
    [preferred, windowsByDay, baseOnRowsByDay, startMin, endMin, step, value, grid, onChange]
  );

  // Track the latest drag for the global pointerup handler without re-binding it.
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;
  useEffect(() => {
    if (readOnly) return;
    const onUp = (): void => {
      const active = dragRef.current;
      if (active) {
        commitDrag(active);
        setDrag(null);
      }
    };
    window.addEventListener('pointerup', onUp);
    return () => window.removeEventListener('pointerup', onUp);
  }, [commitDrag, readOnly]);

  const toggleDay = (dayIdx: number): void => {
    if (readOnly) return;
    const abbr = DAY_ABBR[dayIdx]!;
    const token = dayToken(abbr);
    const nextDays = new Set(preferred);
    const nextDayTimes: Record<string, TimeWindow[]> = {};
    for (const [a, w] of windowsByDay) nextDayTimes[a] = w;
    if (nextDays.has(token)) {
      nextDays.delete(token);
      delete nextDayTimes[abbr];
    } else {
      nextDays.add(token); // preferred, all-day
    }
    const days = DAY_ABBR.filter((a) => nextDays.has(dayToken(a)));
    onChange({ ...value, days, dayTimes: nextDayTimes, grid });
  };

  const updateGrid = (patch: Partial<PreferredDaysGrid>): void => {
    onChange({ ...value, grid: { ...grid, ...patch } });
  };

  const summary = useMemo(
    () => summarizeSelection(value.days, value.dayTimes),
    [value.days, value.dayTimes]
  );

  return (
    <div className="space-y-2">
      {/* Selection summary */}
      <div className="flex flex-wrap items-center gap-1.5">
        {summary.length === 0 ? (
          <span className="text-xs text-muted-foreground">
            {readOnly
              ? 'No bookable times in this view.'
              : 'No days selected yet — drag on the grid below.'}
          </span>
        ) : (
          summary.map((s) => (
            <span
              key={s.day}
              className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-[11px]"
            >
              <span className="font-medium">{s.day}</span>
              <span className="text-muted-foreground">{s.label}</span>
            </span>
          ))
        )}
      </div>

      {/* Grid view controls */}
      {showViewControls && (
        <div className="flex flex-wrap items-end gap-3">
          <TimeRangeSelect
            label="From"
            value={grid.startTime}
            onChange={(v) => updateGrid({ startTime: v })}
          />
          <TimeRangeSelect
            label="To"
            value={grid.endTime}
            onChange={(v) => updateGrid({ endTime: v })}
          />
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Cell size</Label>
            <Select
              value={String(step)}
              onValueChange={(v) => updateGrid({ stepMinutes: Number(v) })}
            >
              <SelectTrigger className="h-7 text-xs w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STEP_OPTIONS.map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    {s} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {rows === 0 ? (
        <p className="text-xs text-destructive">The “To” time must be after the “From” time.</p>
      ) : (
        <>
          <div className="select-none overflow-x-auto rounded-md border">
            {/* Day header */}
            <div className="flex border-b bg-muted/40">
              <div className="w-16 shrink-0" />
              {DAY_ABBR.map((abbr, dayIdx) => {
                const dateLabel = columnDateLabels?.[dayIdx];
                const isPreferred = preferred.has(dayToken(abbr));
                const content = (
                  <>
                    {abbr}
                    {dateLabel ? (
                      <span className="block text-[10px] font-normal text-muted-foreground">
                        {dateLabel}
                      </span>
                    ) : null}
                  </>
                );
                return readOnly ? (
                  <div
                    key={abbr}
                    className={cn(
                      'flex-1 py-1 text-center text-xs font-medium',
                      isPreferred ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {content}
                  </div>
                ) : (
                  <button
                    key={abbr}
                    type="button"
                    onClick={() => toggleDay(dayIdx)}
                    className={cn(
                      'flex-1 py-1 text-xs font-medium transition-colors',
                      isPreferred
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                    title="Toggle the whole day"
                  >
                    {content}
                  </button>
                );
              })}
            </div>
            {/* Time rows — render at natural height (the page scrolls if needed) */}
            <div>
              {Array.from({ length: rows }, (_, rowIdx) => {
                const labelMin = startMin + rowIdx * step;
                const showLabel = labelMin % 60 === 0; // label on the hour
                return (
                  <div key={rowIdx} className="flex">
                    <div className="w-16 shrink-0 pr-1 text-right text-[10px] leading-none text-muted-foreground">
                      {showLabel ? formatTimeLabel(minToTime(labelMin)) : ''}
                    </div>
                    {DAY_ABBR.map((abbr, dayIdx) => {
                      const on = cellOn(dayIdx, rowIdx);
                      const allDay = isAllDay(dayIdx) && !inDragRect(dayIdx, rowIdx);
                      const lit = isHighlighted(dayIdx, rowIdx);
                      const firstLit = lit && !isHighlighted(dayIdx, rowIdx - 1);
                      return (
                        <div
                          key={abbr}
                          ref={firstLit ? highlightRef : undefined}
                          data-highlighted={lit || undefined}
                          onPointerDown={
                            readOnly
                              ? undefined
                              : (e) => {
                                  e.preventDefault();
                                  const mode = baseOnRowsByDay[dayIdx]?.has(rowIdx)
                                    ? 'remove'
                                    : 'add';
                                  setDrag({
                                    mode,
                                    anchor: { day: dayIdx, row: rowIdx },
                                    current: { day: dayIdx, row: rowIdx },
                                  });
                                }
                          }
                          onPointerEnter={
                            readOnly
                              ? undefined
                              : () => {
                                  if (drag)
                                    setDrag({ ...drag, current: { day: dayIdx, row: rowIdx } });
                                }
                          }
                          className={cn(
                            'h-5 flex-1 border-b border-l first:border-l-0',
                            readOnly ? 'cursor-default' : 'cursor-pointer',
                            // Emphasize the line at each hour boundary — that's the
                            // BOTTOM of the row that ends on the hour, so the bold
                            // line lands at the top of the next hour's label row.
                            (labelMin + step) % 60 === 0 ? 'border-b-border' : 'border-b-border/40',
                            on
                              ? allDay
                                ? 'bg-primary/30'
                                : // A light shade of the primary blue, so a highlighted
                                  // slot (solid, below) stands out.
                                  'bg-highlight'
                              : readOnly
                                ? 'bg-background'
                                : 'bg-background hover:bg-muted',
                            lit && 'animate-pulse bg-primary ring-2 ring-inset ring-foreground'
                          )}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {readOnly
              ? showViewControls
                ? 'Shaded blocks are times that could be offered. Adjust From/To/Cell size to change the view.'
                : 'Shaded blocks are times that could be offered.'
              : 'Drag to paint bookable blocks; drag over painted cells to clear. Click a day name to toggle the whole day. A fully-painted day means no time limit.'}
          </p>
        </>
      )}
    </div>
  );
}
